import { getCountryByCode } from 'constants/countries';
import { TradePanelPartner, TradeRegion } from 'store/services/trade/tradeApi';

// Pure helpers behind the Completed tab's partner map, rail and modal. No React
// and no DOM in here, so every rule below is unit-testable.

export const PARTNER_RAIL_SIZE = 8;

/** "WA, US", else the country's name, else null (the caller says "Location not set"). */
export const partnerLocationLabel = (region: TradeRegion | null | undefined): string | null => {
  const country = region?.country?.trim().toUpperCase();
  if (!country) return null;
  const state = region?.state?.trim().toUpperCase();
  if (state) return `${state}, ${country}`;
  return getCountryByCode(country)?.name ?? country;
};

export const LOCATION_NOT_SET = 'Location not set';

/** Median delivery as the modal prints it. Dev data completes instantly, hence "<1 day". */
export const formatMedianDelivery = (days: number | null): string => {
  if (days === null || Number.isNaN(days)) return 'No data';
  if (days < 1) return '<1 day';
  return days === 1 ? '1 day' : `${days} days`;
};

export interface PartnerSummary {
  partners: number;
  trades: number;
  countries: number;
}

/**
 * The modal's header counts. Computed from the SAME array the rows render from,
 * so the header can never disagree with the list under it.
 */
export const summarisePartners = (rows: TradePanelPartner[]): PartnerSummary => {
  const countries = new Set<string>();
  let trades = 0;
  rows.forEach((row) => {
    trades += row.tradeCount;
    const country = row.region?.country?.trim().toUpperCase();
    if (country) countries.add(country);
  });
  return { partners: rows.length, trades, countries: countries.size };
};

/**
 * "Plus N people you have traded with once." N counts the one-trade partners the
 * rail does NOT already show, so "Plus" stays true when a short list fits whole.
 */
export const hiddenOneOffCount = (rows: TradePanelPartner[], shown: number = PARTNER_RAIL_SIZE): number =>
  rows.slice(shown).filter((row) => row.tradeCount === 1).length;

export const filterPartners = (rows: TradePanelPartner[], query: string): TradePanelPartner[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => {
    const location = partnerLocationLabel(row.region) ?? LOCATION_NOT_SET;
    return row.username.toLowerCase().includes(needle) || location.toLowerCase().includes(needle);
  });
};

export type PartnerSortKey = 'username' | 'location' | 'tradeCount' | 'medianDeliveryDays' | 'pointsExchanged';
export type PartnerSortDir = 'asc' | 'desc';

// The backend's own order, used as the tie-break so equal rows never shuffle:
// tradeCount desc, lastCompletedAt desc nulls last, userId asc.
const backendOrder = (a: TradePanelPartner, b: TradePanelPartner): number => {
  if (a.tradeCount !== b.tradeCount) return b.tradeCount - a.tradeCount;
  const aAt = a.lastCompletedAt ? Date.parse(a.lastCompletedAt) : NaN;
  const bAt = b.lastCompletedAt ? Date.parse(b.lastCompletedAt) : NaN;
  const aMissing = Number.isNaN(aAt);
  const bMissing = Number.isNaN(bAt);
  if (aMissing !== bMissing) return aMissing ? 1 : -1;
  if (!aMissing && aAt !== bAt) return bAt - aAt;
  return a.userId - b.userId;
};

const sortValue = (row: TradePanelPartner, key: PartnerSortKey): string | number | null => {
  switch (key) {
    case 'username':
      return row.username.toLowerCase();
    case 'location':
      return partnerLocationLabel(row.region)?.toLowerCase() ?? null;
    case 'medianDeliveryDays':
      return row.medianDeliveryDays;
    case 'pointsExchanged':
      return row.pointsExchanged;
    default:
      return row.tradeCount;
  }
};

/**
 * Missing values sort LAST in both directions: flipping a column must never
 * float the partners with no data to the top of the list.
 */
export const sortPartners = (rows: TradePanelPartner[], key: PartnerSortKey, dir: PartnerSortDir): TradePanelPartner[] => {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null || bv === null) {
      if (av === null && bv === null) return backendOrder(a, b);
      return av === null ? 1 : -1;
    }
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    return backendOrder(a, b);
  });
};

// ---------- Map placement ----------

export interface PartnerDotInput {
  id: number;
  x: number;
  y: number;
  /** Outermost painted radius (stroke included), from partnerDotExtent */
  extent: number;
  tradeCount: number;
}

export interface PlacedPartnerDot {
  id: number;
  x: number;
  y: number;
}

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const SPREAD_GAP = 1;
const SPREAD_STEP = 2;
const SPREAD_MAX_TRIES = 600;

const clamp = (value: number, min: number, max: number) => (max < min ? (min + max) / 2 : Math.min(Math.max(value, min), max));

/**
 * State and country are the finest location the map knows, so every partner in
 * one state lands on one centroid and would overlap exactly, and neighbouring
 * centroids (WA, OR, BC) project only a few pixels apart at world scale.
 * Partners are placed in order of trade count: each takes its own centroid if
 * that spot is clear of EVERY dot already placed, otherwise it walks a
 * golden-angle spiral outward from its centroid until it is. So the partner
 * with the most trades in a cluster keeps the centroid. The first step starts
 * from the dot's own radius, so a 12px dot never sits on its neighbour.
 * Results are clamped inside the map. Deterministic: the same partners always
 * land in the same places.
 */
export const spreadPartnerDots = (
  dots: PartnerDotInput[],
  width: number,
  height: number,
  /** Marks already on the map that these dots must steer clear of and never move */
  obstacles: Array<{ x: number; y: number; extent: number }> = [],
  /** Keep dots inside this rect instead of the whole map: the panel's VISIBLE area, clear of the rail and legend */
  bounds: { left: number; right: number; top: number; bottom: number } = { left: 0, right: width, top: 0, bottom: height }
): PlacedPartnerDot[] => {
  const ordered = [...dots].sort((a, b) => b.tradeCount - a.tradeCount || a.id - b.id);
  const taken: Array<{ x: number; y: number; extent: number }> = [...obstacles];

  return ordered.map((dot) => {
    const fit = (x: number, y: number) => ({
      x: clamp(x, bounds.left + dot.extent, bounds.right - dot.extent),
      y: clamp(y, bounds.top + dot.extent, bounds.bottom - dot.extent),
    });
    const clear = (point: { x: number; y: number }) =>
      taken.every((other) => Math.hypot(other.x - point.x, other.y - point.y) >= other.extent + dot.extent + SPREAD_GAP);

    let point = fit(dot.x, dot.y);
    if (!clear(point)) {
      let distance = dot.extent + SPREAD_GAP;
      for (let attempt = 1; attempt <= SPREAD_MAX_TRIES; attempt += 1) {
        const angle = attempt * GOLDEN_ANGLE;
        point = fit(dot.x + Math.cos(angle) * distance, dot.y + Math.sin(angle) * distance);
        if (clear(point)) break;
        distance += SPREAD_STEP;
      }
    }
    taken.push({ ...point, extent: dot.extent });
    return { id: dot.id, ...point };
  });
};
